import React, { useEffect, useRef, useState } from 'react'
import { useModalActions } from '../../../../shared/component/modal-dialog'
import { useApiFormAuthDevice, useApiFormAuthPassiveLogin } from '../../../../api-new/state/auth'
import { IconLoginDevice, IconCopy, IconCheck, IconRefresh } from '../../../../shared/icon/icon'
import { CodeCountdown } from '../../../../shared/component/countdown'
import { useAuthContext } from '../../../../context/auth.context'
import { useLoginRedirect } from '../hooks/redirect'
import { CodeDisplay } from '../../../../page/component/login-device/code-wells'
import { CODE_LENGTH } from '../../../../page/component/login-device/use-device-code'
import { useCopy } from '../../../../shared/clipboard/clipboard'
import { ManualCopy } from '../../../../shared/clipboard/manual-copy'
import { signInAndRemember } from '../../../../shared/recent-accounts/sign-in-memory'
// Shared with the "Login Another Device" accept modal — .ld-footer/.ld-actions
// stacking + the copy-success flash. Without this import the classes below
// resolved to nothing on the login page, so Copy Code / Refresh Code / Cancel
// rendered as bare text next to an icon.
import '../../../../page/component/login-device/login-device.scss'
import '../../../../shared/sso-workspace/sso-workspace.scss'
import '../../../../shared/sso-workspace/auth-card.scss'
import '../../../../shared/sso-consent/sso-consent.scss'

function useStateRef(initialValue) {
  const [value, setValue] = useState(initialValue)

  const ref = useRef(value)

  useEffect(
    () => {
      ref.current = value
    },
    [value]
  )

  return [value, setValue, ref]
}

const useInterval = (timout, callback) => {
  const [, updateState, refInterval] = useStateRef(null)

  const start = () => {
    if (!refInterval.current) {
      const interval = setInterval(() => {
        callback()
      }, timout)

      updateState(interval)
    }
  }

  const stop = () => {
    if (refInterval.current) {
      clearInterval(refInterval.current)
      updateState(null)
    }
  }

  return [start, stop]
}

export const LoginDeviceModal = ({ target }) => {
  const authContext = useAuthContext()

  const loginRedirect = useLoginRedirect()

  const apiFormAuthDevice = useApiFormAuthDevice()
  const apiFormAuthPassiveLogin = useApiFormAuthPassiveLogin()

  const modalActions = useModalActions(target)

  const [authDevice, updateAuthDevice, refAuthDevice] = useStateRef(null)

  // Once login completes we stop polling; this guards against a late in-flight poll
  // resolving with a 404 (the code was consumed) and spawning a spurious new code.
  const succeeded = useRef(false)

  // Whether the dialog is actually open. Closing it must end the attempt: stop
  // polling, discard the code, and ignore any poll already in flight. Without
  // this, a closed dialog kept polling its code until it expired (~2 min) --
  // and if anyone approved that code in the meantime, THIS page completed the
  // sign-in behind a dialog the user had cancelled. Reopening within that
  // window also showed the stale, half-expired code instead of a fresh one.
  const active = useRef(false)

  // When the code is invalidated we stop and show a notice the user must acknowledge
  // (rather than silently swapping the code). null | 'denied' | 'expired'.
  const [invalidReason, setInvalidReason] = useState(null)

  // The wells row is mounted before any code exists, so it needs a digit count
  // up front. Remembered across the life of this component (survives close +
  // reopen, since the modal doesn't unmount) so a refresh or a reopened dialog
  // keeps the length the server actually issued instead of snapping back to
  // the default.
  const lastCodeLength = useRef(CODE_LENGTH)

  const callAuthDevice = async () => {
    try {
      const clientInfo = getOsType(navigator.userAgent)
      let authDevice = await apiFormAuthDevice.post({ clientInfo: clientInfo })
      if (authDevice && authDevice.code) lastCodeLength.current = authDevice.code.length
      updateAuthDevice(authDevice)
    } catch (e) {
      // Transient (e.g. auth restarting) — never let this reject the caller; the
      // poll will retry. Swallowing avoids an "unhandled rejection" crash overlay.
    }
  }

  const [start, stop] = useInterval(5000, async () => {
    // Ignore a late poll that lost the race to a successful login, or that
    // fired after the dialog was closed.
    if (succeeded.current || !active.current) return

    const current = refAuthDevice.current
    if (!current || !current.code) {
      // No code yet (e.g. the initial create failed during an auth blip) — get one.
      await callAuthDevice()
      return
    }

    try {
      let passiveLogin = await apiFormAuthDevice.get(`${current.code}`)

      // The dialog may have closed while that request was in flight. A cancelled
      // attempt must never complete a sign-in.
      if (!active.current) return

      if (passiveLogin) {
        succeeded.current = true
        stop()
        // Device flow exchanges the passive token at the /device endpoint
        // (service.LoginDevice) so the login is recorded with the correct
        // auth-type — not the generic /:token (service.Login) path.
        let authToken = await apiFormAuthPassiveLogin.get(`${passiveLogin.token}/device`)
        signInAndRemember(authContext.actions.setAuthToken, authToken, { method: { kind: 'device' } })
        modalActions.hide()

        loginRedirect()
      }
    } catch (exc) {
      const status = exc && exc.status
      // The confirming device denied/rejected the request (code deleted) → 404.
      // The code expired before confirmation → 410. Either way, stop and let the user
      // acknowledge. Any other error (403 pending, transient network) keeps polling.
      if (status === 404 || status === 410) {
        stop()
        updateAuthDevice(null)
        setInvalidReason(status === 410 ? 'expired' : 'denied')
      }
    }
  })

  const handleShow = async () => {
    active.current = true
    succeeded.current = false
    setInvalidReason(null)
    if (!refAuthDevice.current) {
      await callAuthDevice()
    }
    start()
  }

  const handleRetry = async () => {
    active.current = true
    succeeded.current = false
    setInvalidReason(null)
    await callAuthDevice()
    start()
  }

  // Closing ends the attempt. The next open always starts with a fresh code.
  const handleHide = () => {
    active.current = false
    stop()
    updateAuthDevice(null)
    setInvalidReason(null)
  }

  useEffect(() => {
    return () => stop()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Bootstrap's modal events drive the attempt, as ModalDialog's did: shown
  // starts it, hidden ends it. Refs keep the listeners on the latest
  // handlers without rebinding.
  const onShownRef = useRef(handleShow)
  const onHiddenRef = useRef(handleHide)
  onShownRef.current = handleShow
  onHiddenRef.current = handleHide

  useEffect(() => {
    const ele = document.getElementById(target)
    if (!ele) return undefined
    const onShown = () => onShownRef.current()
    const onHidden = () => onHiddenRef.current()
    ele.addEventListener('shown.bs.modal', onShown)
    ele.addEventListener('hidden.bs.modal', onHidden)
    return () => {
      ele.removeEventListener('shown.bs.modal', onShown)
      ele.removeEventListener('hidden.bs.modal', onHidden)
    }
  }, [target])

  const titleID = `${target}-title`

  // The sign-in card's language (auth-card.scss .sa-device): a sentence-case
  // title, numbered steps, the code wells (unchanged: their breathing and
  // reveal are code-wells.js / login-device.scss), one primary action (Copy
  // code), Refresh as the secondary one, and Cancel as a quiet close.
  return (
    <div id={target} className="modal fade ld-modal-host" tabIndex="-1" aria-labelledby={titleID} aria-modal="true" role="dialog">
      <div className="modal-dialog modal-dialog-centered ld-dialog">
        <div className="modal-content sw-workspace sa-device">
          {invalidReason
            ? <DeniedNotice reason={invalidReason} titleID={titleID} onRetry={handleRetry} onClose={() => modalActions.hide()} />
            : <React.Fragment>
                <div className="sa-device-head">
                  <span className="sa-device-mark" aria-hidden="true"><IconLoginDevice /></span>
                  <h2 className="sa-device-title" id={titleID}>Sign in from another device</h2>
                </div>

                <ol className="sa-steps">
                  <li>Open EducationXR on a device where you're already signed in.</li>
                  <li>Choose <span className="sa-steps-key"><IconLoginDevice /> Sign in another device</span>.</li>
                  <li>Enter this code there.</li>
                </ol>

                <CodeDisplay code={authDevice && authDevice.code} length={lastCodeLength.current} />

                {/* Display-only: at 0 the 5s poll's 410 flips to the expired notice. */}
                {authDevice && authDevice.expiresAt &&
                  <div className="sa-device-countdown">
                    <CodeCountdown expiresAt={authDevice.expiresAt} />
                  </div>}

                <div className="sa-device-actions is-stacked">
                  <CopyCode code={authDevice && authDevice.code} />
                  <RefreshCode onRefresh={handleRetry} />
                  <button type="button" className="btn sa-cancel" onClick={() => modalActions.hide()}>Cancel</button>
                </div>
              </React.Fragment>}
        </div>
      </div>
    </div>
  )
}

// A labelled footer button, not the superscripted ghost icon this used to be.
//
// The ghost icon was chosen to avoid competing with the digits, but it left the
// single most-wanted action on the screen unlabelled and floating off the corner
// of the code — and the viewer and editor-window modals both put Copy code in
// the footer. Three surfaces of one flow now offer the same three actions in the
// same order with the same words.
//
// Copied inside the click (shared/clipboard), so Safari allows it; "Copied"
// only after a real copy, otherwise the code is shown selected with the
// shortcut.
const CopyCode = ({ code }) => {
  const copy = useCopy()

  return (
    <React.Fragment>
      <button type="button" className={`btn sw-btn-primary ${copy.copied ? 'is-copied' : ''}`} disabled={!code}
        aria-label="Copy code" onClick={() => code && copy.copy(code)}>
        {copy.copied ? <IconCheck /> : <IconCopy />} {copy.copied ? 'Copied' : 'Copy code'}
      </button>
      {copy.manual && code && <ManualCopy text={code} className="text-center" />}
    </React.Fragment>
  )
}

// Seconds before another code may be minted.
const RegenerateCooldownSeconds = 30

// Module scope ON PURPOSE. Component state is destroyed when the modal unmounts,
// so a cooldown held there is bypassed by closing the dialog and re-opening it —
// which is exactly how the viewer's version was bypassed. The deadline has to
// outlive the surface enforcing it.
let regenerateAllowedAt = 0

const secondsUntilRegenerate = () => Math.max(0, (regenerateAllowedAt - Date.now()) / 1000)

// Refresh, gated by a cooldown drawn as a draining ring.
//
// The gate is not about server load — the create endpoint is rate-limited
// server-side. It is that minting a code DELETES the one on screen, so an
// impatient double-click silently invalidates digits somebody is part-way
// through typing on the other device.
const RefreshCode = ({ onRefresh }) => {
  const [remaining, setRemaining] = useState(secondsUntilRegenerate)

  useEffect(() => {
    const interval = setInterval(() => setRemaining(secondsUntilRegenerate()), 200)
    return () => clearInterval(interval)
  }, [])

  const cooling = remaining > 0

  const handleRefresh = () => {
    if (cooling) return
    regenerateAllowedAt = Date.now() + RegenerateCooldownSeconds * 1000
    setRemaining(RegenerateCooldownSeconds)
    onRefresh()
  }

  return (
    <button type="button" className={`btn sw-btn sa-refresh ${cooling ? 'is-cooling' : ''}`.trim()}
      disabled={cooling} aria-label={cooling ? `Refresh code, available in ${Math.ceil(remaining)} seconds` : 'Refresh code'} onClick={handleRefresh}>
      <IconRefresh /> Refresh code
      {cooling && <CooldownRing fraction={remaining / RegenerateCooldownSeconds} seconds={Math.ceil(remaining)} />}
    </button>
  )
}

// A stroke that drains clockwise from the top, the direction a stopwatch face
// trains people to read as time being spent. A number here would read as a
// second timer competing with the code's own expiry.
const CooldownRing = ({ fraction, seconds }) => {
  const radius = 9
  const circumference = 2 * Math.PI * radius

  return (
    <svg width="22" height="22" viewBox="0 0 22 22" aria-hidden="true"
      style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)' }}>
      <circle cx="11" cy="11" r={radius} fill="none" stroke="currentColor" strokeWidth="2"
        strokeLinecap="round" opacity=".85" transform="rotate(-90 11 11)"
        strokeDasharray={circumference}
        strokeDashoffset={circumference * (1 - Math.max(0, Math.min(1, fraction)))} />
      <text x="11" y="11" textAnchor="middle" dominantBaseline="central"
        fontSize="10" fill="currentColor">{seconds}</text>
    </svg>
  )
}

const DeniedNotice = ({ reason, titleID, onRetry, onClose }) => {
  const denied = reason === 'denied'
  return (
    <div className="sa-device-notice">
      <span className={`sc-outcome-mark ${denied ? 'bad' : 'warn'}`} aria-hidden="true">{denied ? '×' : '!'}</span>
      <h2 className="sa-device-title" id={titleID}>{denied ? 'Sign-in denied' : 'Code expired'}</h2>
      <p className="sw-hint m-0">
        {denied
          ? 'This sign-in was denied on the other device. Try again for a new code.'
          : 'This code expired before it was entered. Try again for a new code.'}
      </p>
      {/* Cancel is a button, as on the code screen (owner 2026-10-07: no
          link-styled actions). */}
      <div className="sa-device-actions is-stacked">
        <button type="button" className="btn sw-btn-primary" onClick={onRetry}>Try again</button>
        <button type="button" className="btn sa-cancel" onClick={onClose}>Cancel</button>
      </div>
    </div>
  )
}

const getOsType = userAgent => {
  if (/Macintosh/.test(userAgent)) {
    return 'Macintosh'
  }

  if (/Windows/.test(userAgent)) {
    return 'Windows'
  }

  if (/Linux/.test(userAgent)) {
    return 'Linux'
  }

  if (/iPhone/.test(userAgent)) {
    return 'iPhone'
  }

  if (/iPad/.test(userAgent)) {
    return 'iPad'
  }

  if (/Android/.test(userAgent)) {
    return 'Android'
  }

  return 'Admin Website'
}
